export const BOARD_WIDTH = 10;
export const BOARD_HEIGHT = 20;

export const PIECES = {
  I: {
    shape: [
      [0, 0, 0, 0],
      [1, 1, 1, 1],
      [0, 0, 0, 0],
      [0, 0, 0, 0],
    ],
    color: '#00f5ff',
  },
  O: {
    shape: [
      [1, 1],
      [1, 1],
    ],
    color: '#ffff00',
  },
  T: {
    shape: [
      [0, 1, 0],
      [1, 1, 1],
      [0, 0, 0],
    ],
    color: '#a000f0',
  },
  S: {
    shape: [
      [0, 1, 1],
      [1, 1, 0],
      [0, 0, 0],
    ],
    color: '#00f000',
  },
  Z: {
    shape: [
      [1, 1, 0],
      [0, 1, 1],
      [0, 0, 0],
    ],
    color: '#f00000',
  },
  J: {
    shape: [
      [1, 0, 0],
      [1, 1, 1],
      [0, 0, 0],
    ],
    color: '#0000f0',
  },
  L: {
    shape: [
      [0, 0, 1],
      [1, 1, 1],
      [0, 0, 0],
    ],
    color: '#ff7f00',
  },
} as const;

export const PIECE_TYPES = Object.keys(PIECES);

export interface Position {
  x: number;
  y: number;
}

export interface Piece {
  type: string;
  shape: number[][];
  color: string;
  position: Position;
}

export interface TetrisGameState {
  board: (string | null)[][];
  currentPiece: Piece | null;
  nextPieces: string[];
  heldPiece: string | null;
  canHold: boolean;
  score: number;
  lines: number;
  level: number;
  isGameOver: boolean;
  isPaused: boolean;
}

export type TetrisAction =
  | { type: 'INIT' }
  | { type: 'MOVE'; direction: 'left' | 'right' | 'down' }
  | { type: 'ROTATE'; clockwise: boolean }
  | { type: 'HARD_DROP' }
  | { type: 'HOLD' }
  | { type: 'TOGGLE_PAUSE' };

export function randomPieceType(): string {
  return PIECE_TYPES[Math.floor(Math.random() * PIECE_TYPES.length)];
}

export function createEmptyBoard(): (string | null)[][] {
  return Array(BOARD_HEIGHT)
    .fill(null)
    .map(() => Array(BOARD_WIDTH).fill(null));
}

export function createPiece(type?: string): Piece {
  const pieceType = type || randomPieceType();
  const piece = PIECES[pieceType as keyof typeof PIECES];
  const shape = piece.shape.map((row) => [...row]);
  return {
    type: pieceType,
    shape,
    color: piece.color,
    position: { x: Math.floor(BOARD_WIDTH / 2) - Math.floor(shape[0].length / 2), y: 0 },
  };
}

export function createInitialQueue(size = 5): string[] {
  return Array(size)
    .fill(null)
    .map(() => randomPieceType());
}

export function checkCollision(piece: Piece, board: (string | null)[][], offsetX = 0, offsetY = 0): boolean {
  for (let y = 0; y < piece.shape.length; y++) {
    for (let x = 0; x < piece.shape[y].length; x++) {
      if (piece.shape[y][x]) {
        const newX = piece.position.x + x + offsetX;
        const newY = piece.position.y + y + offsetY;

        if (newX < 0 || newX >= BOARD_WIDTH || newY >= BOARD_HEIGHT) {
          return true;
        }

        if (newY >= 0 && board[newY][newX]) {
          return true;
        }
      }
    }
  }
  return false;
}

export function rotatePiece(piece: Piece, clockwise = true): Piece {
  const rotated = piece.shape[0].map((_, index) => piece.shape.map((row) => row[index]));

  if (!clockwise) {
    rotated.reverse();
  } else {
    rotated.forEach((row) => row.reverse());
  }

  return { ...piece, shape: rotated };
}

export function placePiece(piece: Piece, board: (string | null)[][]): (string | null)[][] {
  const newBoard = board.map((row) => [...row]);

  for (let y = 0; y < piece.shape.length; y++) {
    for (let x = 0; x < piece.shape[y].length; x++) {
      if (piece.shape[y][x]) {
        const boardY = piece.position.y + y;
        const boardX = piece.position.x + x;
        if (boardY >= 0 && boardY < BOARD_HEIGHT && boardX >= 0 && boardX < BOARD_WIDTH) {
          newBoard[boardY][boardX] = piece.color;
        }
      }
    }
  }

  return newBoard;
}

export function clearLines(board: (string | null)[][]): { board: (string | null)[][]; linesCleared: number } {
  const newBoard = [...board];
  let linesCleared = 0;

  for (let y = BOARD_HEIGHT - 1; y >= 0; y--) {
    if (newBoard[y].every((cell) => cell !== null)) {
      newBoard.splice(y, 1);
      newBoard.unshift(Array(BOARD_WIDTH).fill(null));
      linesCleared++;
      y++;
    }
  }

  return { board: newBoard, linesCleared };
}

export function calculateScore(linesCleared: number, level: number): number {
  const baseScores = [0, 40, 100, 300, 1200];
  return baseScores[linesCleared] * (level + 1);
}

function ensureQueue(nextPieces: string[]): string[] {
  const queue = [...nextPieces];
  while (queue.length < 5) {
    queue.push(randomPieceType());
  }
  return queue;
}

function lockAndSpawn(state: TetrisGameState, piece: Piece, extraScore = 0): TetrisGameState {
  const newBoard = placePiece(piece, state.board);
  const { board: clearedBoard, linesCleared } = clearLines(newBoard);
  const newLines = state.lines + linesCleared;
  const nextLevel = Math.min(Math.floor(newLines / 10) + 1, 10);
  const newScore = state.score + calculateScore(linesCleared, state.level) + extraScore;

  const queue = ensureQueue(state.nextPieces);
  const nextPieceType = queue.shift()!;
  queue.push(randomPieceType());
  const nextPiece = createPiece(nextPieceType);
  const isGameOver = checkCollision(nextPiece, clearedBoard);

  return {
    ...state,
    board: clearedBoard,
    currentPiece: isGameOver ? null : nextPiece,
    nextPieces: queue,
    canHold: true,
    score: newScore,
    lines: newLines,
    level: nextLevel,
    isGameOver,
  };
}

export function createInitialTetrisState(): TetrisGameState {
  const queue = createInitialQueue();
  const firstPiece = createPiece(queue.shift()!);
  queue.push(randomPieceType());

  return {
    board: createEmptyBoard(),
    currentPiece: firstPiece,
    nextPieces: queue,
    heldPiece: null,
    canHold: true,
    score: 0,
    lines: 0,
    level: 1,
    isGameOver: false,
    isPaused: false,
  };
}

export function applyTetrisAction(state: TetrisGameState, action: TetrisAction): TetrisGameState {
  switch (action.type) {
    case 'INIT':
      return createInitialTetrisState();
    case 'TOGGLE_PAUSE':
      return { ...state, isPaused: !state.isPaused };
    case 'MOVE': {
      if (state.isGameOver || state.isPaused || !state.currentPiece) {
        return state;
      }

      const offsets = {
        left: { x: -1, y: 0 },
        right: { x: 1, y: 0 },
        down: { x: 0, y: 1 },
      };
      const offset = offsets[action.direction];

      if (!checkCollision(state.currentPiece, state.board, offset.x, offset.y)) {
        return {
          ...state,
          currentPiece: {
            ...state.currentPiece,
            position: {
              x: state.currentPiece.position.x + offset.x,
              y: state.currentPiece.position.y + offset.y,
            },
          },
        };
      }

      if (action.direction === 'down') {
        return lockAndSpawn(state, state.currentPiece);
      }

      return state;
    }
    case 'ROTATE': {
      if (state.isGameOver || state.isPaused || !state.currentPiece) {
        return state;
      }

      const rotated = rotatePiece(state.currentPiece, action.clockwise);
      if (checkCollision(rotated, state.board)) {
        return state;
      }

      return {
        ...state,
        currentPiece: rotated,
      };
    }
    case 'HARD_DROP': {
      if (state.isGameOver || state.isPaused || !state.currentPiece) {
        return state;
      }

      let dropDistance = 0;
      while (!checkCollision(state.currentPiece, state.board, 0, dropDistance + 1)) {
        dropDistance++;
      }

      const droppedPiece: Piece = {
        ...state.currentPiece,
        position: {
          ...state.currentPiece.position,
          y: state.currentPiece.position.y + dropDistance,
        },
      };

      return lockAndSpawn(state, droppedPiece, dropDistance * 2);
    }
    case 'HOLD': {
      if (state.isGameOver || state.isPaused || !state.currentPiece || !state.canHold) {
        return state;
      }

      const currentType = state.currentPiece.type;
      const queue = [...state.nextPieces];

      let newPieceType: string;
      if (state.heldPiece) {
        newPieceType = state.heldPiece;
      } else {
        const ensured = ensureQueue(queue);
        newPieceType = ensured.shift()!;
        ensured.push(randomPieceType());
        return {
          ...state,
          currentPiece: createPiece(newPieceType),
          nextPieces: ensured,
          heldPiece: currentType,
          canHold: false,
        };
      }

      return {
        ...state,
        currentPiece: createPiece(newPieceType),
        nextPieces: queue,
        heldPiece: currentType,
        canHold: false,
      };
    }
    default:
      return state;
  }
}